import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import backgroundImage from "./assets/bg-image.png";
import titleRobo from "./assets/title.jpg";
import section1 from "./assets/section-1.png";
import section2 from "./assets/section-2.png";
import section3 from "./assets/section-3.png";
import section7 from "./assets/section-7.png";
import section8 from "./assets/section-8.png";
import roboShare from "./assets/robo-share.png";
import roboComment from "./assets/robo-comment.png";
import theme from "./assets/theme.png";
import googlePlayBanner from "./assets/google-play-banner.png";
import appStoreBanner from "./assets/app-store-banner.png";
import iconLock from "./assets/icon-lock.png";
import iconBackup from "./assets/icon-backup.png";
import iconSearch from "./assets/icon-search.png";
import iconFree from "./assets/icon-free.png";

import summary from "./assets/summary.png";
import "./TopPage.scss";
import { Helmet } from "react-helmet";

import ReactGA from "react-ga4";

const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=net.aidiary";
const APP_STORE_URL = "https://apps.apple.com/jp/app/id6736706558";

function TopPage() {
  const navigate = useNavigate();
  const search = useLocation().search;
  const query = useMemo(() => new URLSearchParams(search), [search]);
  const page = query.get("page");
  const tutorial = query.get("tutorial");
  const [visibleSections, setVisibleSections] = useState<Set<number>>(
    new Set(),
  );
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: TopPage.name,
      title: TopPage.name,
    });
  }, []);

  useEffect(() => {
    // /?page=pp の場合 /ppにリダイレクト
    if (page) {
      console.log("page", page);
      navigate("/" + page);
    }
  }, [navigate, page, query]);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    sectionRefs.current.forEach((section, index) => {
      if (!section) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleSections((prev) => new Set(prev).add(index));
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: "0px 0px -50px 0px",
        },
      );

      observer.observe(section);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  return (
    <div
      className="TopPage-wrap"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundRepeat: "repeat",
        backgroundPosition: "0 0",
        backgroundSize: "auto",
      }}
    >
      <div
        className="App"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundRepeat: "repeat",
          backgroundPosition: "0 0",
          backgroundSize: "auto",
        }}
      >
        <Helmet>
          <title>AI日記 キロノ：かわいい・おしゃれな日記アプリ</title>
          <meta
            name="description"
            content="かわいいAIキャラ【キロノ】に話しかけるだけでおしゃれな写真付き日記が完成！夢診断・メンタルケア・三行日記など機能充実。毎日5件まで無料！"
          />
          <meta http-equiv="Content-Script-Type" content="text/javascript" />
          <meta http-equiv="Content-Style-Type" content="text/css" />

          <meta
            property="og:title"
            content="AI日記 キロノ：かわいい・おしゃれな日記アプリ"
          />
          <meta
            property="og:description"
            content="かわいいAIキャラ【キロノ】に話しかけるだけでおしゃれな写真付き日記が完成！夢診断・メンタルケア・三行日記など機能充実。毎日5件まで無料！"
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://www.ai-diary.jp/" />
          <meta
            property="og:image"
            content="https://www.ai-diary.jp/image/thumbnail.png"
          />
          <meta
            property="og:site_name"
            content="AI日記 キロノ：かわいい・おしゃれな日記アプリ"
          />
          <meta property="og:locale" content="ja_JP" />

          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" />
          <link
            href="https://fonts.googleapis.com/css2?family=Barlow:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=M+PLUS+2:wght@100..900&family=Montserrat:ital,wght@0,100..900;1,100..900&family=Nobile:ital,wght@0,400;0,500;0,700;1,400;1,500;1,700&family=Noto+Sans+JP:wght@100..900&family=Orbitron:wght@400..900&family=Petrona:ital,wght@0,100..900;1,100..900&family=Zen+Maru+Gothic&display=swap"
            rel="stylesheet"
          />
        </Helmet>
        {tutorial !== "true" && (
          <div className="language-area">
            <a href="/">日本語</a>
            <p>/</p>
            <a href="/en">English</a>
            <p>/</p>
            <a href="/zh">中文</a>
          </div>
        )}
        <div
          className="title-area"
          style={{
            backgroundImage: `url(${titleRobo})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <p className="sub-title">
            ひとこと話しかけるだけでおしゃれな日記が完成！
            <br />
            かわいいAIキャラ【キロノ】が毎日を彩る
            <br />
            生成AI搭載 日記アプリ
          </p>
        </div>
        {tutorial !== "true" && (
          <div className="store-image-wrapper">
            <img
              src={googlePlayBanner}
              onClick={() => {
                window.open(GOOGLE_PLAY_URL, "_blank");
              }}
              className="google-playbanner-image"
              alt="google play"
            />
            <img
              src={appStoreBanner}
              onClick={() => {
                window.open(APP_STORE_URL, "_blank");
              }}
              className="google-playbanner-image"
              alt="App Store"
            />
          </div>
        )}

        {/* ① 多彩な日記作成モード */}
        <div
          className={`section scroll-animate ${
            visibleSections.has(0) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[0] = el)}
        >
          <TitleLabel
            label="特徴"
            number="①"
            sub="三日坊主でも大丈夫！"
            title="箇条書きで完成！多彩な日記作成モード"
          />
          <img
            src={section1}
            className="section-image trim-vertical"
            alt="AI日記作成"
          />
          <div className="description-wrapper">
            <ul>
              <li>
                <b>【AI生成モード】</b>
                ：かわいいキロノとチャットしたり、短いメモを入力するだけでAIが素敵な日記に仕上げます
              </li>
              <li>
                <b>【三行日記モード】</b>
                ：「嫌だったこと」「嬉しかったこと」「明日の目標」を1行ずつ書くだけ
              </li>
              <li>
                <b>【予定から作成】</b>
                ：Googleカレンダーなどの予定を読み込んで、自動で日記を生成
              </li>
              <li>
                AIボイスによる読み上げ機能で、まるで物語の朗読を聞いているような体験ができます
              </li>
            </ul>
          </div>
        </div>

        {/* ② AIがおしゃれに彩る */}
        <div
          className={`section scroll-animate ${
            visibleSections.has(1) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[1] = el)}
        >
          <TitleLabel
            label="特徴"
            number="②"
            sub="作風も画像もAIにおまかせ！"
            title="AIがあなたの毎日をおしゃれに彩る"
          />
          {/* TODO: 作風・AI画像生成のスクリーンショット画像に差し替え */}
          <div className="section-image-description-row">
            <img
              className="left-image"
              alt="AIおしゃれ日記イメージ"
              src={section2}
            />
            <div className="description-wrapper">
              <ul>
                <li>
                  「小説風」「コメディ風」など、気分に合わせて日記の作風を変更可能！
                </li>
                <li>
                  写真がなくても大丈夫！日記の内容に合わせてAIがおしゃれな写真・画像を自動生成。思い出を視覚的に残せます
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* ③ こころのサポート */}
        <div
          className={`section scroll-animate ${
            visibleSections.has(2) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[2] = el)}
        >
          <TitleLabel
            label="特徴"
            number="③"
            sub="キロノがあなたの心に寄り添う！"
            title="メンタルをケアするこころのサポート"
          />
          {/* TODO: こころ分析・気分グラフのスクリーンショット画像に差し替え */}
          <div className="section-image-description-row">
            <img
              className="left-image"
              alt="メンタルケア・こころ分析イメージ"
              src={section3}
            />
            <div className="description-wrapper">
              <ul>
                <li>
                  <b>【夢診断機能】</b>
                  ：見た夢をメモするだけで、AIがあなたの深層心理を分析
                </li>
                <li>
                  <b>【気分の可視化】</b>
                  ：日々のメンタルの変化を記録し、グラフで振り返り
                </li>
                <li>
                  <b>【キロノのこころ分析】</b>
                  ：最近の日記からメンタルの状態を多角的にレポート（プレミアム限定）
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* ④ キロノのアドバイス */}
        <div
          className={`section scroll-animate ${
            visibleSections.has(3) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[3] = el)}
        >
          <TitleLabel
            label="特徴"
            number="④"
            sub="コメントがもらえる！"
            title="キロノのアドバイスでモチベーションアップ"
          />
          <img
            src={roboComment}
            className="section-image"
            alt="キロノのアドバイス"
          />
          <div className="description-wrapper">
            <ul>
              <li>
                楽しかったこともモヤモヤしたことも、日記に吐き出せばキロノが優しいコメントをくれます
              </li>
              <li>
                悩んでいるときは気持ちを切り替えるアドバイスをくれるかも!?
              </li>
            </ul>
          </div>
        </div>

        {/* ⑤ シェア */}
        <div
          className={`section scroll-animate ${
            visibleSections.has(4) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[4] = el)}
        >
          <TitleLabel
            label="特徴"
            number="⑤"
            sub="交換日記も簡単！"
            title="お友達やみんなと日記をシェア"
          />
          <img src={roboShare} className="section-image" alt="シェア機能" />
          <div className="description-wrapper">
            <ul>
              <li>日記をお友達やみんなに公開することも可能</li>
              <li>公開範囲は限定できるからプライバシーもしっかり保護</li>
              <li>
                温かいコメントをもらって、日記を続けるモチベーションがアップ！
              </li>
            </ul>
          </div>
        </div>

        {/* ⑥ カスタマイズ */}
        <div
          className={`section scroll-animate ${
            visibleSections.has(5) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[5] = el)}
        >
          <TitleLabel
            label="特徴"
            number="⑥"
            sub="もっと自分らしい日記帳に！"
            title="5,000通り以上のおしゃれカスタマイズ"
          />
          <img
            src={theme}
            className="section-image trim-top"
            alt="テーマカスタマイズ"
          />
          <div className="description-wrapper">
            <ul>
              <li>
                フォント、文字色、文字サイズ、背景色、背景画像など、5,000通り以上のバリエーションから自分好みのデザインに！
              </li>
              <li>
                純国産アプリならではの、日本人の感性に合わせたかわいいデザインを多数収録
              </li>
            </ul>
          </div>
        </div>

        {/* ⑦ 振り返り */}
        <div
          className={`section scroll-animate ${
            visibleSections.has(6) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[6] = el)}
        >
          <TitleLabel
            label="特徴"
            number="⑦"
            sub="過去の自分を発見！"
            title="振り返りがもっと楽しくなる"
          />
          <div className="section-image-description-row">
            <img src={section7} className="left-image" alt="週まとめ機能" />
            <div className="description-wrapper">
              <ul>
                <li>
                  一週間に3回以上日記を書くと、AIがあなたの一週間をまとめてお知らせ
                </li>
                <li>
                  「1年前の今日の日記」をお知らせする機能で、自身の成長や変化を実感
                </li>
                <li>カレンダー機能で過去の日記も未来の予定も一括管理</li>
              </ul>
            </div>
          </div>
        </div>

        {/* ⑧ セキュリティ */}
        <div
          className={`section scroll-animate ${
            visibleSections.has(7) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[7] = el)}
        >
          <TitleLabel
            label="特徴"
            number="⑧"
            sub="大事な日記をしっかり守る！"
            title="充実のセキュリティ機能"
          />
          <div className="section-image-description-row">
            <img src={section8} className="left-image" alt="セキュリティ" />
            <div className="description-wrapper">
              <ul>
                <li>パターンロック機能で、他人に日記を覗かれる心配なし</li>
                <li>SNSに日記を投稿する際に、個人名をAIが匿名化してくれる</li>
                <li>サーバーとの通信は完全に暗号化</li>
                <li>
                  入力した日記内容がAIの学習データとして利用されることは一切ありません
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* ⑨ バックアップ・同期 */}
        <div
          className={`section scroll-animate ${
            visibleSections.has(8) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[8] = el)}
        >
          <TitleLabel
            label="特徴"
            number="⑨"
            sub="機種変も安心！"
            title="日記は全て自動バックアップ・マルチデバイス同期"
          />
          <div className="section-image-description-row">
            <img src={iconBackup} className="left-image" alt="バックアップ" />
            <div className="description-wrapper">
              <ul>
                <li>
                  日記はサーバーに保存されるので、スマホを無くしたり機種変しても日記が消えることはありません
                </li>
                <li>
                  複数の端末で日記を同期。スマホとタブレットの両方で快適に使えます
                </li>
                <li>
                  一括シェア機能を使えば、Google
                  Keepなどのアプリにも簡単に保存できます
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* ⑩ 検索 */}
        <div
          className={`section scroll-animate ${
            visibleSections.has(9) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[9] = el)}
        >
          <TitleLabel
            label="特徴"
            number="⑩"
            sub="あの日の日記もすぐ見つかる！"
            title="豊富な検索機能"
          />
          <div className="section-image-description-row">
            <img src={iconSearch} className="left-image" alt="検索機能" />
            <div className="description-wrapper">
              <ul>
                <li>
                  キーワードで日記を素早く検索。大切な思い出をいつでも振り返れます
                </li>
                <li>
                  カレンダーから日付を指定して、あの日の日記をすぐに見つけられます
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* ⑪ 無料 */}
        <div
          className={`section scroll-animate ${
            visibleSections.has(10) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[10] = el)}
        >
          <TitleLabel
            label="特徴"
            number="⑪"
            sub="無料で使える！"
            title="基本機能は全て無料！"
          />
          <div className="section-image-description-row">
            <img src={iconFree} className="left-image" alt="無料" />
            <div className="description-wrapper">
              <ul>
                <li>
                  日記の作成、AIとの会話、画像の生成など、基本機能は全て無料で使えます
                </li>
                <li>
                  毎日5個まで無料で日記作成可能。無料でもずっと使い続けられます
                </li>
                <li>
                  AIによる「添削機能」や、英語・中国語での日記作成にも対応でスキルアップも
                </li>
                <li>さらにヘビーに使いたい方向けのプレミアムプランもご用意</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 注意点 */}
        <div
          className={`section scroll-animate ${
            visibleSections.has(11) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[11] = el)}
        >
          <TitleLabel title="ご利用に際しての注意点" />
          <div className="description-wrapper">
            <ul>
              <li>
                本アプリは日記の生成にAIを使いますが、AIが出力する情報は必ずしも正しいとは限りません。必ず出力内容をご確認の上、日記の保存、公開を行ってください。
              </li>
              <li>
                必ず利用規約とプライバシーポリシーをご確認の上、同意してからご利用ください。
              </li>
            </ul>
          </div>
          <div className="annotation-wrapper">
            <ul>
              <li>
                ※1.サービス終了やメンテナンスなどの理由でサーバーが停止する可能性があります。ご了承ください。（シェア機能を使って定期的に外部サービスに日記をシェアしておくことをおすすめします）
              </li>
            </ul>
          </div>
          {tutorial !== "true" && (
            <div className="tosppWrapper">
              <Link to="/tos">利用規約</Link>・
              <Link to="/privacy">プライバシーポリシー</Link>・
              <Link to="/deletedata">データの削除方法</Link>
            </div>
          )}
          {tutorial !== "true" && (
            <div className="store-image-wrapper">
              <img
                src={googlePlayBanner}
                onClick={() => {
                  window.open(GOOGLE_PLAY_URL, "_blank");
                }}
                className="google-playbanner-image"
                alt="google play"
              />
              <img
                src={appStoreBanner}
                onClick={() => {
                  window.open(APP_STORE_URL, "_blank");
                }}
                className="google-playbanner-image"
                alt="google play"
              />
            </div>
          )}
        </div>

        {/* お問合せ */}
        {tutorial !== "true" && (
          <div
            className={`section scroll-animate ${
              visibleSections.has(12) ? "animate-in" : ""
            }`}
            ref={(el) => (sectionRefs.current[12] = el)}
          >
            <TitleLabel title="お問合せ先" />

            <div className="description-wrapper">
              <a href="mailto:support@ai-daiary.net">support@ai-daiary.net</a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

type TitleLabelProps = {
  label?: string;
  number?: string;
  sub?: string;
  title: string;
};
const TitleLabel = (props: TitleLabelProps) => {
  return (
    <div className="section-title-wrapper">
      {props.label && (
        <p className="feature-label">
          {props.label}
          <span>{props.number}</span>
        </p>
      )}
      <div className="section-title-right-wrapper">
        {props.sub && <p className="section-title-sub">{props.sub}</p>}
        <p className="title">{props.title}</p>
      </div>
    </div>
  );
};

export default TopPage;
